/*
 * App update plumbing, shared by index.js and App.js.
 *
 * The service worker is the source of truth: a new build means a new worker, and the
 * browser hands it to us as `registration.waiting`. We add what the default wiring lacks:
 *   - periodic update checks while the app is open (phones keep PWAs open for days)
 *   - a check whenever the app comes back to the foreground
 *   - a reload that fires only when the new worker has taken control
 *   - the incoming version and notes, fetched from the new build's changelog
 */

const CHECK_EVERY_MS = 60 * 60 * 1000; // once an hour while visible
let registrationRef = null;
let listeners = new Set();

export const onUpdateReady = (fn) => { listeners.add(fn); return () => listeners.delete(fn); };
const announce = (reg) => { registrationRef = reg; listeners.forEach((fn) => fn(reg)); };

/** Wire up a registration: report a waiting worker now or whenever one appears */
export const watchRegistration = (reg) => {
  registrationRef = reg;
  if (reg.waiting && navigator.serviceWorker.controller) announce(reg);
  reg.addEventListener('updatefound', () => {
    const sw = reg.installing;
    if (!sw) return;
    sw.addEventListener('statechange', () => {
      if (sw.state === 'installed' && navigator.serviceWorker.controller) announce(reg);
    });
  });

  const check = () => { if (document.visibilityState === 'visible') reg.update().catch(() => {}); };
  setInterval(check, CHECK_EVERY_MS);
  document.addEventListener('visibilitychange', () => { if (document.visibilityState === 'visible') check(); });
};

/** Ask the service worker to check for a new build right now */
export const checkForUpdate = () => registrationRef?.update().catch(() => {});

/**
 * Activate the waiting worker and reload once it controls the page. Resolves false if
 * there is nothing waiting. The reload is tied to controllerchange, with a generous
 * timeout as a last resort, so a slow network cannot reload into the old build.
 */
export const applyUpdate = () => new Promise((resolve) => {
  const waiting = registrationRef?.waiting;
  if (!waiting) { resolve(false); return; }
  let done = false;
  const finish = () => { if (done) return; done = true; window.location.reload(); };
  navigator.serviceWorker.addEventListener('controllerchange', finish, { once: true });
  waiting.postMessage({ type: 'SKIP_WAITING' });
  setTimeout(finish, 8000);
});

/** The changelog of the build on the server, so the banner can name what is coming */
export const fetchIncomingChangelog = async () => {
  try {
    const res = await fetch(`/changelog.json?t=${Date.now()}`, { cache: 'no-store' });
    if (!res.ok) return null;
    return await res.json();
  } catch {
    return null;
  }
};

// "Later" hides the banner for this session; the home screen keeps a quiet reminder.
const LATER_KEY = 'app-update-later';
export const deferUpdate = () => { try { sessionStorage.setItem(LATER_KEY, '1'); } catch { /* ignore */ } };
export const isUpdateDeferred = () => { try { return sessionStorage.getItem(LATER_KEY) === '1'; } catch { return false; } };
